import React, {useEffect, useState} from "react";
import {Link} from "react-router-dom";
import {Button, FormControl, Label, Table} from "react-bootstrap";
import {
    assignSim,
    getSims,
    getUnitSimHistory,
    unassignSim,
} from "../utils/bedrockAPI";
import {formatPhoneNumber} from "../utils/Common";
import {apiErrorMessage, formatDate, sticker} from "../utils/unitDisplay";
import {isHardwareSim, simStatusStyle} from "../utils/simDisplay";

export default function UnitSimPanel({unitId, sim, onSimChanged}) {
    const [drawerSims, setDrawerSims] = useState([]);
    const [history, setHistory] = useState(null);
    const [pickedSimId, setPickedSimId] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadSideData = async () => {
        try {
            const [allSims, historyRows] = await Promise.all([
                getSims().catch(() => []),
                getUnitSimHistory(unitId).catch(() => []),
            ]);
            setDrawerSims((allSims || []).filter((row) => !row.unitId && isHardwareSim(row)));
            setHistory((Array.isArray(historyRows) ? historyRows : []).filter((row) => isHardwareSim(row.sim)));
        } catch (err) {
            setError(apiErrorMessage(err));
        }
    };

    useEffect(() => {
        let cancelled = false;
        setHistory(null);
        setError(null);
        (async () => {
            try {
                const [allSims, historyRows] = await Promise.all([
                    getSims().catch(() => []),
                    getUnitSimHistory(unitId).catch(() => []),
                ]);
                if (cancelled) return;
                setDrawerSims((allSims || []).filter((row) => !row.unitId && isHardwareSim(row)));
                setHistory((Array.isArray(historyRows) ? historyRows : []).filter((row) => isHardwareSim(row.sim)));
            } catch (err) {
                if (!cancelled) setError(apiErrorMessage(err));
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [unitId]);

    const handleAssign = async () => {
        if (!pickedSimId) return;
        setSaving(true);
        setError(null);
        try {
            const updated = await assignSim(pickedSimId, unitId);
            onSimChanged(updated);
            setPickedSimId("");
            await loadSideData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSaving(false);
    };

    const handleUnassign = async () => {
        if (!sim?.simId) return;
        if (!window.confirm("Unassign this SIM from the unit? It goes back to the drawer.")) {
            return;
        }
        setSaving(true);
        setError(null);
        try {
            await unassignSim(sim.simId);
            onSimChanged(null);
            await loadSideData();
        } catch (err) {
            setError(apiErrorMessage(err));
        }
        setSaving(false);
    };

    const hardwareSim = sim && !isHardwareSim(sim) ? null : sim;
    const phone = hardwareSim?.phoneNumber
        ? formatPhoneNumber(hardwareSim.phoneNumber) || hardwareSim.phoneNumber
        : null;

    return (
        <div className="units-section">
            <h3 className="units-expand-heading">SIM</h3>
            {error && <div className="units-error">{error}</div>}
            {hardwareSim ? (
                <div className="units-expand-grid">
                    <div>
                        <span className="units-meta-label">Carrier</span>
                        <div className="units-meta-value">{hardwareSim.carrier || "—"}</div>
                    </div>
                    <div>
                        <span className="units-meta-label">Phone</span>
                        <div className="units-meta-value">{phone || "—"}</div>
                    </div>
                    <div>
                        <span className="units-meta-label">ICCID</span>
                        <div className="units-meta-value">{hardwareSim.iccid || "—"}</div>
                    </div>
                    <div>
                        <span className="units-meta-label">Status</span>
                        <div>
                            <Label bsStyle={simStatusStyle(hardwareSim.status)}>{hardwareSim.status || "—"}</Label>
                        </div>
                    </div>
                    <div>
                        <Button bsSize="small" bsStyle="warning" disabled={saving} onClick={handleUnassign}>
                            Unassign SIM
                        </Button>
                    </div>
                </div>
            ) : sim === undefined ? (
                <div className="units-empty">Loading SIM…</div>
            ) : (
                <div className="units-empty">No SIM assigned.</div>
            )}

            <div className="units-note-form" style={{marginTop: 8}}>
                <FormControl
                    componentClass="select"
                    value={pickedSimId}
                    onChange={(e) => setPickedSimId(e.target.value)}
                    className="units-note-type"
                    style={{flex: "1 1 280px"}}
                >
                    <option value="">Assign a drawer SIM…</option>
                    {drawerSims.map((row) => (
                        <option key={row.simId} value={row.simId}>
                            {[row.carrier, row.phoneNumber ? formatPhoneNumber(row.phoneNumber) || row.phoneNumber : null, row.iccid || "no ICCID"]
                                .filter(Boolean)
                                .join(" · ")}
                        </option>
                    ))}
                </FormControl>
                <Button
                    bsStyle="primary"
                    bsSize="small"
                    disabled={saving || !pickedSimId}
                    onClick={handleAssign}
                >
                    {hardwareSim ? "Replace SIM" : "Assign SIM"}
                </Button>
                <Link to="/admin/sims" className="btn btn-default btn-sm">
                    SIM inventory
                </Link>
            </div>

            <h3 className="units-expand-heading">Past SIMs</h3>
            {history == null ? (
                <div className="units-empty">Loading SIM history…</div>
            ) : history.length === 0 ? (
                <div className="units-empty">No SIM assignments yet.</div>
            ) : (
                <Table striped condensed className="units-table">
                    <thead>
                        <tr>
                            <th>Assigned</th>
                            <th>Removed</th>
                            <th>Carrier</th>
                            <th>Phone</th>
                            <th>ICCID</th>
                        </tr>
                    </thead>
                    <tbody>
                        {history.map((row) => (
                            <tr key={row.simAssignmentId}>
                                <td>{formatDate(row.assignedAt)}</td>
                                <td>{row.unassignedAt ? formatDate(row.unassignedAt) : "current"}</td>
                                <td>{row.sim?.carrier || "—"}</td>
                                <td>
                                    {row.sim?.phoneNumber
                                        ? formatPhoneNumber(row.sim.phoneNumber) || row.sim.phoneNumber
                                        : "—"}
                                </td>
                                <td>{row.sim?.iccid || sticker(row.simId)}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </div>
    );
}
